import React, { useEffect, useMemo, useState } from "react";
import { StandardPageWrapper } from "../../../components/layout/StandardPageWrapper";
import { Button, Modal, TextInput, TextInputBase, ToolTip, useSnackBar } from "@truevault/aquilo";
import Pill from "@truevault/aquilo/src/components/pill/Pill";
import { Form, Field } from "react-final-form";
import { useActionRequest } from "../../../../common/hooks/api";
import { Api } from "../../../../common/service/Api";
import { Error as OrganizationError } from "../../../copy/organization";
import { Error as InputError } from "../../../copy/input";
import { validateNotIn } from "../../../util/validation";
import { RouteHelpers, Routes } from "../../../root/routes";
import {
  CheckCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  EllipsisVerticalIcon,
  LockClosedIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import {
  useInvalidateOnboardingProgress,
  useOnboardingProgress,
} from "../../../../common/hooks/useOnboardingProgress";
import { ProgressFooter, ProgressFooterActions } from "../../get-compliant/ProgressFooter";
import { DataFlowsHelpCard } from "./DataFlowsExplanatoryCards";
import { NavCountBadge } from "./DataFlowsDisclosuresPage";
import { DATA_FLOWS_CONTENT_MAX_WIDTH, DataFlowsStickyHeader } from "./DataFlowsPageHeader";
import { useDataFlowsSourcesCompletion } from "./dataFlowsCompletion";
import { Menu } from "@headlessui/react";
import { Float } from "@headlessui-float/react";
import { useHistory } from "react-router";
import {
  useCreateDefaultCollectionGroups,
  useEmploymentCollectionGroups,
  useNonEmploymentCollectionGroups,
} from "../../../hooks/useCollectionGroups";
import { usePrimaryOrganization, usePrimaryOrganizationId } from "../../../hooks";
import { Loading, PageLoading } from "../../../components";
import { CollectionGroupDetailsDto } from "../../../../common/service/server/dto/CollectionGroupDto";
import { DataFlowsDiagram } from "./DataFlowsDiagram";
import { useDataMap } from "../../../../common/hooks/api/useDataMap";
import { DataMapDto } from "../../../../common/service/server/dto/datamap/DataMapDto";
import { useFilteredPicGroups } from "../../../components/organisms/collection-groups/ConsumerDataCollectionCheckboxes";
import {
  collectedPICGroupsFromFilteredCollection,
  collectedPICsFromFilteredCollection,
  disclosedPICGroupsFromFilteredDisclosed,
  receivedPICsFromFilteredReceived,
  useDataMapCollectedPICIds,
} from "../../../../common/service/resources/data-map/dataMapTransformers";
import { useReceivedForCollectionGroups } from "../../../hooks/useReceivedForCollectionGroups";
import { useDisclosuresForCollectionGroups } from "../../../hooks/useDisclosuresForCollectionGroups";
import {
  getDataRecipientsWithNoDisclosedPICSelection,
  getIncompleteDisclosurePICGroups,
} from "./DataFlowsDisclosuresPage";
import {
  labelForLawfulBasis,
  UseDefaultActivityCheckboxes,
  useDefaultActivityCheckboxes,
  useProcessingActivites,
} from "../../../hooks/useProcessingActivities";
import {
  isCustomProcessingActivity,
  isPurposesPageComplete,
} from "./purposes/DataFlowsPurposesPage";
import clsx from "clsx";
import {
  CollectionContext,
  OrganizationDataRecipientDto,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { CollectionGroupType } from "../../../../common/service/server/controller/CollectionGroupsController";
import { ProcessingActivityDto } from "../../../../common/service/server/dto/ProcessingActivityDto";
import { useSurvey, SurveyAnswers } from "../../../../common/hooks/api/useSurvey";
import {
  EMPLOYEE_COLLECTION_PURPOSES_SURVEY,
  isHrPurposesComplete,
} from "../../../components/organisms/collection-groups/EmploymentGroupPurposesQuestion";
import { SOURCE_KEY_CONSUMER_DIRECTLY } from "../../../types/Vendor";

/**
 * Purposes shown for a finished consumer group: the checked default purposes plus the org's custom
 * ones. Custom purposes have no default checkbox, so filtering `defaults` alone dropped them from the
 * data-map diagram and the collapsed-panel summary (POL-7818 #7). Step 3 treats a custom purpose as
 * selected when it covers the US region — mirror that here.
 */
function selectedConsumerPurposes(
  defCheckboxes: UseDefaultActivityCheckboxes,
  activities: ProcessingActivityDto[] | undefined,
): { id: string; name: string }[] {
  const defaults = defCheckboxes.props.defaults
    .filter((d) => defCheckboxes.props.checked.includes(d.id))
    .map((d) => ({ id: d.id, name: d.name }));
  const custom = (activities ?? [])
    .filter((a) => isCustomProcessingActivity(a) && a.regions.includes("UNITED_STATES"))
    .map((a) => ({ id: a.id, name: a.name }));
  return [...defaults, ...custom];
}

export type DataFlowsDashboardPageConfig = {
  collectionContext: CollectionContext[];
  collectionGroupType: CollectionGroupType;
  breadcrumbTitle: string;
  breadcrumbSource: string;
  breadcrumbSourceRoute: string;
  showBreadcrumbs?: boolean;
  dashboardRoute: string;
  sourcesRoute?: string;
  /** Consumer only: purposes are org-wide, so they get their own tab instead of a per-group step. */
  purposesRoute?: string;
  collectionRouteForGroup: (groupId: string) => string;
  disclosuresRouteForGroup: (groupId: string) => string;
  purposesRouteForGroup: (groupId: string) => string;
  showConsumerOnboarding: boolean;
  // Empty-state copy for configs that don't use the consumer onboarding card (HR only —
  // the consumer path always renders the one-click "Create my Consumers group" card).
  emptyStateTitle?: string;
  emptyStateBody?: string;
};

export const CONSUMER_DATA_FLOWS_DASHBOARD_CONFIG: DataFlowsDashboardPageConfig = {
  collectionContext: ["CONSUMER"],
  collectionGroupType: "BUSINESS_TO_CONSUMER",
  breadcrumbTitle: "Data Flows",
  breadcrumbSource: "Data Map",
  breadcrumbSourceRoute: Routes.dataMap.dashboard,
  dashboardRoute: Routes.dataMap.dataFlows.dashboard,
  sourcesRoute: Routes.dataMap.dataFlows.sources,
  collectionRouteForGroup: RouteHelpers.dataMap.dataFlows.collectionForGroup,
  disclosuresRouteForGroup: RouteHelpers.dataMap.dataFlows.disclosuresForGroup,
  purposesRoute: Routes.dataMap.dataFlows.purposes,
  purposesRouteForGroup: () => Routes.dataMap.dataFlows.purposes,
  showConsumerOnboarding: true,
};

export const HR_DATA_FLOWS_DASHBOARD_CONFIG: DataFlowsDashboardPageConfig = {
  collectionContext: ["EMPLOYEE"],
  collectionGroupType: "EMPLOYMENT",
  breadcrumbTitle: "Data Flows",
  breadcrumbSource: "HR Center",
  breadcrumbSourceRoute: Routes.hrCenter.root,
  showBreadcrumbs: false,
  dashboardRoute: Routes.hrCenter.hrDataMap.dataFlows,
  collectionRouteForGroup: RouteHelpers.hrCenter.hrDataMap.dataFlowsCollectionForGroup,
  disclosuresRouteForGroup: RouteHelpers.hrCenter.hrDataMap.dataFlowsDisclosuresForGroup,
  purposesRouteForGroup: RouteHelpers.hrCenter.hrDataMap.dataFlowsPurposesForGroup,
  showConsumerOnboarding: false,
  emptyStateTitle: "No HR collection groups found.",
  emptyStateBody:
    "Complete HR data map collection setup to create employment groups before mapping data flows.",
};

export function getCollectionGroupDataFlowCompletion({
  dataMap,
  collectionGroup,
  dataRecipients,
  defCheckboxes,
  activities,
  isHr = false,
  hrPurposesAnswers,
}: {
  dataMap: DataMapDto;
  collectionGroup: CollectionGroupDetailsDto;
  dataRecipients: OrganizationDataRecipientDto[];
  defCheckboxes: UseDefaultActivityCheckboxes;
  activities: ProcessingActivityDto[];
  isHr?: boolean;
  hrPurposesAnswers?: SurveyAnswers;
}) {
  const associatedVendorIds = new Set(
    dataMap.recipientAssociations
      .filter((a) => a.groupId == collectionGroup.id && a.noDataDisclosure !== true)
      .map((a) => a.vendorId),
  );
  const associatedDataRecipients = dataRecipients.filter((dr) =>
    associatedVendorIds.has(dr.vendorId),
  );
  const collected = collectedPICsFromFilteredCollection(dataMap, {
    collectionGroupId: collectionGroup.id,
  });
  const collectedPICGroups = collectedPICGroupsFromFilteredCollection(dataMap, {
    collectionGroupId: collectionGroup.id,
  });

  const isCollectionFinished = collected.length > 0;
  const isDisclosureFinished =
    isCollectionFinished &&
    getIncompleteDisclosurePICGroups(
      dataMap,
      associatedDataRecipients,
      [collectionGroup],
      collectedPICGroups,
    ).length == 0 &&
    getDataRecipientsWithNoDisclosedPICSelection(
      dataMap,
      associatedDataRecipients,
      [collectionGroup],
      collectedPICGroups,
    ).length === 0;
  const isPurposesFinished = isHr
    ? isHrPurposesComplete(hrPurposesAnswers, collectionGroup.id)
    : isPurposesPageComplete(defCheckboxes, activities);

  return {
    isCollectionFinished,
    isDisclosureFinished,
    isPurposesFinished,
    // Consumer purposes are org-wide (their own tab), so a consumer group is done after its two
    // steps; HR purposes are answered per group and stay part of the group.
    isAllComplete: isCollectionFinished && isDisclosureFinished && (!isHr || isPurposesFinished),
  };
}

export function useDataFlowCollectionGroupsCompletion({
  config,
  groups,
}: {
  config: DataFlowsDashboardPageConfig;
  groups: CollectionGroupDetailsDto[] | undefined;
}) {
  const orgId = usePrimaryOrganizationId();
  const isHr = config.collectionContext.includes("EMPLOYEE");
  const { data: dataMap } = useDataMap(orgId);
  const disclosureData = useDisclosuresForCollectionGroups(
    orgId,
    groups ?? [],
    config.collectionContext,
  );
  const [activities, , refreshActivities] = useProcessingActivites(orgId);
  const defCheckboxes = useDefaultActivityCheckboxes(
    orgId,
    activities,
    /* region = */ "UNITED_STATES",
    /* createRegions = */ ["UNITED_STATES", "EEA_UK", "CANADA"],
    refreshActivities,
  );
  // Consumer purposes never read this survey — don't fetch it there (it renders per group).
  const { answers: hrPurposesAnswers, request: hrPurposesSurveyRequest } = useSurvey(
    orgId,
    EMPLOYEE_COLLECTION_PURPOSES_SURVEY,
    /* onAnswer = */ undefined,
    /* notReady = */ !isHr,
  );

  const isReady = isHr
    ? Boolean(groups) &&
      Boolean(dataMap) &&
      disclosureData.requests.dataMapRequest.success &&
      disclosureData.requests.recipientsRequest.success &&
      hrPurposesSurveyRequest.success
    : Boolean(groups) &&
      Boolean(dataMap) &&
      Boolean(activities) &&
      disclosureData.requests.dataMapRequest.success &&
      disclosureData.requests.recipientsRequest.success &&
      defCheckboxes.requests.defaultActivities.success;

  const completions =
    isReady && groups && dataMap
      ? groups.map((collectionGroup) => ({
          collectionGroup,
          completion: getCollectionGroupDataFlowCompletion({
            dataMap,
            collectionGroup,
            dataRecipients: disclosureData.dataRecipients,
            defCheckboxes,
            activities: activities ?? [],
            isHr,
            hrPurposesAnswers,
          }),
        }))
      : null;

  const allComplete =
    completions != null &&
    completions.length > 0 &&
    completions.every(({ completion }) => completion.isAllComplete);

  // Org-wide purposes state. For consumers it is one answer for the whole flow (the Purposes
  // tab); for HR it is only complete when every group's purposes are.
  const purposesComplete =
    completions != null &&
    (isHr
      ? completions.length > 0 &&
        completions.every(({ completion }) => completion.isPurposesFinished)
      : isPurposesPageComplete(defCheckboxes, activities ?? []));

  // The first (group, step) a user still has to do, in flow order — drives the
  // dashboard footer's "Next: …" narration. Step 3 is per group for HR; for consumers it is
  // the org-wide Purposes tab, reported with `collectionGroup: null` once every group is done.
  let nextIncomplete: {
    collectionGroup: CollectionGroupDetailsDto | null;
    step: 1 | 2 | 3;
  } | null = null;
  if (completions != null) {
    for (const { collectionGroup, completion } of completions) {
      if (!completion.isCollectionFinished) {
        nextIncomplete = { collectionGroup, step: 1 };
        break;
      }
      if (!completion.isDisclosureFinished) {
        nextIncomplete = { collectionGroup, step: 2 };
        break;
      }
      if (isHr && !completion.isPurposesFinished) {
        nextIncomplete = { collectionGroup, step: 3 };
        break;
      }
    }
    if (nextIncomplete == null && !isHr && completions.length > 0 && !purposesComplete) {
      nextIncomplete = { collectionGroup: null, step: 3 };
    }
  }

  // Per-group step states, in page order — the help card's chips use the first group's to
  // decide which shortcuts are unlocked.
  const firstGroupCompletion = completions?.[0]?.completion ?? null;

  // Steps 2 and 3 only (pol-7567) — Step 1 needs no separate check, since isDisclosureFinished
  // already requires it. Callers pair this with their own Sources check.
  const allDisclosuresAndPurposesComplete =
    completions != null &&
    completions.length > 0 &&
    completions.every(
      ({ completion }) => completion.isDisclosureFinished && completion.isPurposesFinished,
    );

  return {
    allComplete,
    purposesComplete,
    allDisclosuresAndPurposesComplete,
    isReady,
    nextIncomplete,
    firstGroupCompletion,
  };
}

export function useConsumerDataFlowsCompletion(groups: CollectionGroupDetailsDto[] | undefined) {
  const orgId = usePrimaryOrganizationId();
  const collectionGroupsCompletion = useDataFlowCollectionGroupsCompletion({
    config: CONSUMER_DATA_FLOWS_DASHBOARD_CONFIG,
    groups,
  });
  // Shared with the dashboard footer's Sources narration, so the task list and the page it links
  // to can never disagree about whether Sources is done.
  const sourcesCompletion = useDataFlowsSourcesCompletion(orgId, groups);
  // groups undefined = still loading; [] = no non-employment groups → ready but incomplete
  const isReady =
    groups !== undefined && sourcesCompletion.isReady && collectionGroupsCompletion.isReady;

  return {
    isComplete:
      sourcesCompletion.isSourcesComplete &&
      collectionGroupsCompletion.allDisclosuresAndPurposesComplete,
    isReady,
  };
}

function RenameCollectionGroupModal({
  organizationId,
  collectionGroup,
  existingGroups,
  onClose,
  onSaved,
}: {
  organizationId: string;
  collectionGroup: CollectionGroupDetailsDto;
  existingGroups: CollectionGroupDetailsDto[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const otherExistingGroupNames = useMemo(
    () => existingGroups.map((g) => g.name).filter((n) => n !== collectionGroup.name),
    [existingGroups, collectionGroup.name],
  );

  const { fetch: saveRename, request: saveRequest } = useActionRequest({
    api: (values: { name: string }) =>
      Api.consumerGroups.updateCollectionGroup(organizationId, collectionGroup.id, {
        name: values.name,
        enabled: true,
        privacyCenterEnabled: true,
        collectionGroupType: collectionGroup.collectionGroupType,
        gdprEeaUkDataCollection: collectionGroup.gdprEeaUkDataCollection,
      }),
    messages: {
      forceError: OrganizationError.UpdateCollectionGroup,
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <div className="tw-boundary">
      <Modal
        title="Rename Collection Group"
        alignText="left"
        alignContent="left"
        showOverlay
        closeButton
        closeOnOverlayClick
        onClose={onClose}
        contentContainerClasses="w-full"
        contentPadding="p-[24px]"
      >
        <Form
          onSubmit={(values: { name?: string }) => saveRename({ name: (values.name ?? "").trim() })}
          initialValues={{ name: collectionGroup.name }}
        >
          {({ handleSubmit, submitting, pristine, invalid }) => (
            <form onSubmit={handleSubmit} className="w-full">
              <Field
                name="name"
                validate={(val) => {
                  const trimmed = (val ?? "").trim();
                  if (!trimmed) {
                    return InputError.Required;
                  }
                  return validateNotIn(
                    otherExistingGroupNames,
                    "A Collection Group with this name already exists.",
                  )(trimmed);
                }}
              >
                {({ meta }) => (
                  <div>
                    <TextInput
                      field="name"
                      label="Name"
                      placeholder="Name"
                      required
                      inputBorder={
                        meta.touched && meta.error
                          ? "border border-solid border-red-500 ring-1 ring-red-500"
                          : undefined
                      }
                    />
                    {meta.touched && meta.error && (
                      <p className="text-sm text-red-600 mt-1">{meta.error}</p>
                    )}
                  </div>
                )}
              </Field>
              <div className="flex justify-end gap-3 mt-6 pt-2">
                <Button type="button" color="tertiary" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  onClick={() => {}}
                  disabled={pristine || invalid || submitting || saveRequest.running}
                >
                  Save
                </Button>
              </div>
            </form>
          )}
        </Form>
      </Modal>
    </div>
  );
}

function RemoveCollectionGroupConfirmationModal({
  collectionGroupName,
  onConfirm,
  onCancel,
}: {
  collectionGroupName: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [removeConfirmation, setRemoveConfirmation] = useState("");

  return (
    <Modal
      title={`Are you sure you want to remove ${collectionGroupName}?`}
      body="Any information you've mapped to this collection group will be lost. This action cannot be undone."
      alignText="left"
      alignButton="right"
      secondButton="Delete Collection Group"
      secondButtonColor="warning"
      onSecondButtonClick={onConfirm}
      secondButtonDisabled={removeConfirmation.toLowerCase() !== "remove"}
      firstButton="Cancel"
      onFirstButtonClick={onCancel}
      showOverlay
      onClose={onCancel}
    >
      <div className="text-neutral-700 text-[14px] font-normal">
        Type "remove" below to remove the collection group.
      </div>
      <TextInputBase
        field="removeCollectionGroupConfirmation"
        placeholder="Type here"
        value={removeConfirmation}
        onChange={(e) => setRemoveConfirmation(e.target.value)}
      />
    </Modal>
  );
}

function AddCollectionGroupModal({
  organizationId,
  existingGroups,
  collectionGroupType,
  onClose,
  onCreated,
}: {
  organizationId: string;
  existingGroups: CollectionGroupDetailsDto[];
  collectionGroupType: CollectionGroupType;
  onClose: () => void;
  onCreated: () => void | Promise<void>;
}) {
  const existingGroupNames = useMemo(() => existingGroups.map((g) => g.name), [existingGroups]);

  const { fetch: createGroup, request: createRequest } = useActionRequest({
    api: (values: { name: string }) =>
      Api.consumerGroups.createCollectionGroup(organizationId, {
        name: values.name,
        enabled: true,
        privacyCenterEnabled: true,
        collectionGroupType,
        gdprDataSubject: false,
        processingRegions: undefined,
      }),
    messages: {
      forceError: OrganizationError.AddDataSubjectType,
    },
    onSuccess: async () => {
      await onCreated();
      onClose();
    },
  });

  return (
    <div className="tw-boundary">
      <Modal
        title={
          collectionGroupType === "EMPLOYMENT"
            ? "Add New HR Collection Group"
            : "Add New Collection Group"
        }
        alignText="left"
        alignContent="left"
        showOverlay
        closeButton
        closeOnOverlayClick
        onClose={onClose}
        contentContainerClasses="w-full"
        contentPadding="p-[24px]"
      >
        <Form
          onSubmit={(values: { name?: string }) =>
            createGroup({ name: (values.name ?? "").trim() })
          }
          initialValues={{ name: "" }}
        >
          {({ handleSubmit, submitting, pristine, invalid }) => (
            <form onSubmit={handleSubmit} className="w-full">
              <Field
                name="name"
                validate={(val) => {
                  const trimmed = (val ?? "").trim();
                  if (!trimmed) {
                    return InputError.Required;
                  }
                  return validateNotIn(
                    existingGroupNames,
                    "A Collection Group with this name already exists.",
                  )(trimmed);
                }}
              >
                {({ meta }) => (
                  <div>
                    <TextInput
                      field="name"
                      label="Name"
                      placeholder="Collection group name"
                      required
                      inputBorder={
                        meta.touched && meta.error
                          ? "border border-solid border-red-500 ring-1 ring-red-500"
                          : undefined
                      }
                    />
                    {meta.touched && meta.error && (
                      <p className="text-sm text-red-600 mt-1">{meta.error}</p>
                    )}
                  </div>
                )}
              </Field>
              <div className="flex justify-end gap-3 mt-6 pt-2">
                <Button type="button" color="tertiary" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  onClick={() => {}}
                  disabled={pristine || invalid || submitting || createRequest.running}
                >
                  Create
                </Button>
              </div>
            </form>
          )}
        </Form>
      </Modal>
    </div>
  );
}

/**
 * A chip in the help card's flow summary. Unlocked chips navigate to the matching step page and
 * take a purple hover; locked ones dim and explain what has to happen first, mirroring the step
 * cards below so the two stay consistent.
 */
function StepChip({
  stepNumber,
  label,
  route,
  isLocked = false,
  lockedTooltip,
}: {
  stepNumber?: number;
  label: string;
  route: string;
  isLocked?: boolean;
  lockedTooltip?: string;
}) {
  const history = useHistory();

  const chip = (
    <button
      type="button"
      // aria-disabled, not disabled: a natively disabled button gets no pointer events and leaves
      // the tab order, so the ToolTip explaining what to finish first could never open — the one
      // thing a locked chip exists to say. The onClick guard below is what actually locks it.
      aria-disabled={isLocked}
      onClick={() => !isLocked && history.push(route)}
      className={clsx(
        "inline-flex items-center rounded-full border border-solid border-neutral-200 bg-white",
        // Text color comes from a utility class, not an inline style, so the purple
        // hover:text-primary-700 can win — an inline color would always beat it. Every chip
        // reads at the same weight, numbered or not.
        "text-neutral-800",
        isLocked
          ? "opacity-50 cursor-not-allowed"
          : "cursor-pointer hover:border-primary-600 hover:text-primary-700",
      )}
      style={{
        gap: 7,
        padding: stepNumber ? "5px 11px 5px 7px" : "5px 11px",
        fontSize: 14,
        fontWeight: 500,
      }}
    >
      {stepNumber != null && (
        <span
          className="inline-flex items-center justify-center rounded-full"
          style={{
            width: 18,
            height: 18,
            fontSize: 11,
            fontWeight: 600,
            backgroundColor: "#EEEDFE",
            color: "#3C3489",
          }}
        >
          {stepNumber}
        </span>
      )}
      {label}
    </button>
  );

  if (isLocked && lockedTooltip) {
    return (
      <ToolTip text={lockedTooltip} maxWidth="240px" inline>
        {chip}
      </ToolTip>
    );
  }

  return chip;
}

type SubCardStatus = "incomplete" | "complete";

function DataFlowSubCard({
  stepNumber,
  title,
  description,
  status,
  targetRoute,
  isDisabled = false,
  disabledTooltip,
}: {
  stepNumber: number;
  title: string;
  description: string;
  status?: SubCardStatus;
  targetRoute: string;
  isDisabled?: boolean;
  disabledTooltip?: string;
}) {
  const history = useHistory();
  const navigate = () => {
    if (!isDisabled) {
      history.push(targetRoute);
    }
  };
  const isComplete = status === "complete";
  const ctaLabel = isDisabled ? "Locked" : isComplete ? "Review" : "Start";

  // The whole card is the button (hover lift, press, focus ring), and it also carries an explicit
  // CTA whose label follows state — "Start" for the next step, "Review" once done, "Locked" until
  // unlocked — so the affordance is unmistakable. The CTA replaces the old "Incomplete" pill.
  const card = (
    <div
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      aria-disabled={isDisabled}
      className={clsx(
        "group flex h-full flex-col rounded-[8px] border border-solid border-neutral-200 bg-white p-[20px] text-left shadow-sm transition-all duration-150",
        isDisabled
          ? "cursor-not-allowed opacity-50"
          : "cursor-pointer hover:-translate-y-px hover:border-primary-600 hover:shadow-md active:translate-y-0 active:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
      )}
      onClick={navigate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate();
        }
      }}
    >
      <div className="flex items-center justify-between gap-[10px]">
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            lineHeight: "22px",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: "#726D83",
          }}
        >
          Step {stepNumber}
        </div>
        {isDisabled ? (
          <LockClosedIcon className="h-[18px] w-[18px] flex-shrink-0 text-neutral-400" />
        ) : isComplete ? (
          <CheckCircleIcon className="h-[18px] w-[18px] flex-shrink-0 text-emerald-600" />
        ) : (
          <ChevronRightIcon className="h-[18px] w-[18px] flex-shrink-0 text-neutral-600 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-primary-600" />
        )}
      </div>
      <div className="mt-[2px] mb-[2px] text-[16px] font-medium text-gray-900">{title}</div>
      <div className="text-[13px] text-neutral-500">{description}</div>
      {/* mt-auto keeps the CTA on one baseline across the three cards whatever their text length. */}
      <div className="mt-auto pt-[8px]">
        <Button
          color={isComplete ? "secondary" : "primary"}
          size="xs"
          disabled={isDisabled}
          onClick={(e) => {
            e.stopPropagation();
            navigate();
          }}
        >
          {ctaLabel}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="tw-boundary h-full">
      {isDisabled ? (
        <ToolTip
          text={disabledTooltip ?? `Complete Step ${stepNumber - 1} to unlock this step`}
          maxWidth="240px"
          topLevelStyles={{ height: "100%" }}
        >
          {card}
        </ToolTip>
      ) : (
        card
      )}
    </div>
  );
}

/** Eyebrow-titled block inside the collapsed group snapshot (crit c_567bc2). */
function SummarySection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-[12px]">
      <div
        className="text-[11px] font-semibold"
        style={{ letterSpacing: "0.04em", textTransform: "uppercase", color: "#726D83" }}
      >
        {title}
      </div>
      <div className="mt-[4px] text-[13px]" style={{ lineHeight: "19px", color: "#534E65" }}>
        {children}
      </div>
    </div>
  );
}

function ConsumersPanel({
  collectionGroup,
  dataMap,
  allCollectionGroups,
  config,
  onCollectionGroupsUpdated,
}: {
  collectionGroup: CollectionGroupDetailsDto;
  dataMap: DataMapDto;
  allCollectionGroups: CollectionGroupDetailsDto[];
  config: DataFlowsDashboardPageConfig;
  onCollectionGroupsUpdated: () => void;
}) {
  const history = useHistory();
  const orgId = usePrimaryOrganizationId();
  const [org] = usePrimaryOrganization();
  const isHr = config.collectionContext.includes("EMPLOYEE");
  const [isExpanded, setIsExpanded] = useState(true);
  // The collapse animation needs the body in the DOM while it plays, but a collapsed panel should
  // not keep paying to render its diagrams. Drop the heavy subtree once the transition is over.
  const [shouldRenderBody, setShouldRenderBody] = useState(true);
  useEffect(() => {
    if (isExpanded) {
      setShouldRenderBody(true);
      return;
    }
    const timer = window.setTimeout(() => setShouldRenderBody(false), 300);
    return () => window.clearTimeout(timer);
  }, [isExpanded]);
  const [renameTarget, setRenameTarget] = useState<CollectionGroupDetailsDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CollectionGroupDetailsDto | null>(null);

  const filteredPicGroups = useFilteredPicGroups({ dataMap, org });
  const collected = useDataMapCollectedPICIds(dataMap, { collectionGroupId: collectionGroup.id });
  const receivedData = useReceivedForCollectionGroups(
    org?.id,
    [collectionGroup],
    config.collectionContext,
  );
  const disclosureData = useDisclosuresForCollectionGroups(
    org?.id,
    [collectionGroup],
    config.collectionContext,
  );
  const [activities, , refreshActivities] = useProcessingActivites(org?.id);
  const defCheckboxes = useDefaultActivityCheckboxes(
    org?.id,
    activities,
    /* region = */ "UNITED_STATES",
    /* createRegions = */ ["UNITED_STATES", "EEA_UK", "CANADA"],
    refreshActivities,
  );
  // Consumer purposes never read this survey — don't fetch it there (it renders per group).
  const { answers: hrPurposesAnswers, request: hrPurposesSurveyRequest } = useSurvey(
    orgId,
    EMPLOYEE_COLLECTION_PURPOSES_SURVEY,
    /* onAnswer = */ undefined,
    /* notReady = */ !isHr,
  );
  const { fetch: removeGroup } = useActionRequest({
    api: (collectionGroup: CollectionGroupDetailsDto) =>
      Api.consumerGroups.deleteDataSubjectType(orgId, collectionGroup.id),
    messages: {
      forceError: OrganizationError.RemoveDataSubjectType,
    },
    onSuccess: () => {
      onCollectionGroupsUpdated();
    },
  });

  if (
    !org ||
    !dataMap ||
    !filteredPicGroups ||
    !collected ||
    (!isHr && !activities) ||
    (isHr && !hrPurposesSurveyRequest.success) ||
    !receivedData ||
    !disclosureData ||
    !disclosureData.requests.dataMapRequest.success ||
    !disclosureData.requests.recipientsRequest.success
  ) {
    return <Loading />;
  }

  const { isCollectionFinished, isDisclosureFinished, isPurposesFinished, isAllComplete } =
    getCollectionGroupDataFlowCompletion({
      dataMap,
      collectionGroup,
      dataRecipients: disclosureData.dataRecipients,
      defCheckboxes,
      activities: activities ?? [],
      isHr,
      hrPurposesAnswers,
    });

  // Collapsed-panel snapshot (crit c_d7373e): collected category groups, how many vendors take
  // disclosures, and (consumer only) the chosen purposes and lawful bases.
  const collectedGroupNames = filteredPicGroups
    .filter((group) => group.categories.some((cat) => collected.includes(cat.id)))
    .map((group) => group.name);
  const disclosureVendorIds = new Set(
    dataMap.recipientAssociations
      .filter((a) => a.groupId == collectionGroup.id && a.noDataDisclosure !== true)
      .map((a) => a.vendorId),
  );
  const disclosureVendorCount = disclosureData.dataRecipients.filter((dr) =>
    disclosureVendorIds.has(dr.vendorId),
  ).length;
  const summaryPurposeNames = isHr
    ? []
    : selectedConsumerPurposes(defCheckboxes, activities).map((p) => p.name);
  const summaryLawfulBases =
    !isHr && org.featureGdpr
      ? Array.from(new Set((activities ?? []).flatMap((a) => a.lawfulBases))).map(
          labelForLawfulBasis,
        )
      : [];

  return (
    <div className="tw-boundary">
      {renameTarget && orgId && (
        <RenameCollectionGroupModal
          organizationId={orgId}
          collectionGroup={renameTarget}
          existingGroups={allCollectionGroups}
          onClose={() => setRenameTarget(null)}
          onSaved={onCollectionGroupsUpdated}
        />
      )}
      {deleteTarget && (
        <RemoveCollectionGroupConfirmationModal
          collectionGroupName={deleteTarget.name}
          onConfirm={async () => {
            await removeGroup(deleteTarget);
            setDeleteTarget(null);
          }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
      {/* Panel surface matches the pol-7592 vendor inventory cards, but one shade darker
          (neutral-300) than the inner step cards so the group's outline reads clearly. */}
      <div
        className="rounded-[12px] w-full bg-white mt-[24px] border border-solid border-neutral-300"
        style={{
          maxWidth: DATA_FLOWS_CONTENT_MAX_WIDTH,
          boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
        }}
      >
        <div
          className="p-[16px] rounded-t-[12px] flex justify-between items-center"
          style={isExpanded ? { borderBottom: "1px solid #E8E5EE" } : undefined}
        >
          <div className="flex items-center gap-[8px]">
            <span className="text-[18px] font-semibold text-neutral-900">
              {collectionGroup.name}
            </span>
            <Menu as="div" className="relative inline-block text-left">
              <Float placement="bottom-start" offset={{ mainAxis: 4 }}>
                <Menu.Button
                  className="flex items-center rounded p-0.5 hover:bg-neutral-100 focus:outline-none"
                  onClick={(e) => e.stopPropagation()}
                >
                  <EllipsisVerticalIcon className="h-[20px] w-[20px] text-neutral-500" />
                </Menu.Button>

                <Menu.Items
                  className="rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                  style={{ zIndex: 250, minWidth: "160px" }}
                >
                  <div className="py-1">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={clsx(
                            "w-full text-left px-4 py-2 text-sm text-neutral-700",
                            active && "bg-primary-100",
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            setRenameTarget(collectionGroup);
                          }}
                        >
                          Rename
                        </button>
                      )}
                    </Menu.Item>
                    {collectionGroup.autocreationSlug != "ONLINE_SHOPPERS" && (
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={clsx(
                              "w-full text-left px-4 py-2 text-sm text-red-600",
                              active && "bg-red-50",
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(collectionGroup);
                            }}
                          >
                            Delete
                          </button>
                        )}
                      </Menu.Item>
                    )}
                  </div>
                </Menu.Items>
              </Float>
            </Menu>
          </div>
          <div
            className="flex items-center gap-[8px] cursor-pointer"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {!isAllComplete && <Pill variant="warning">Incomplete</Pill>}
            {isExpanded ? (
              <ChevronUpIcon className="h-5 w-5 text-neutral-600" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-neutral-600" />
            )}
          </div>
        </div>

        {!isExpanded && (
          <div className="px-[16px] pb-[16px]">
            {/* Organized snapshot (crit c_567bc2): count pill on top, then a labeled list per
                dimension. */}
            <NavCountBadge
              count={disclosureVendorCount}
              label={disclosureVendorCount === 1 ? "vendor disclosure" : "vendor disclosures"}
              tone="neutral"
            />
            <SummarySection title="Data collected">
              {collectedGroupNames.length > 0 ? (
                collectedGroupNames.join(", ")
              ) : (
                <span className="text-neutral-500">No data categories selected yet</span>
              )}
            </SummarySection>
            {summaryPurposeNames.length > 0 && (
              <SummarySection title="Purposes">{summaryPurposeNames.join(", ")}</SummarySection>
            )}
            {summaryLawfulBases.length > 0 && (
              <SummarySection title="Lawful Bases">{summaryLawfulBases.join(", ")}</SummarySection>
            )}
          </div>
        )}

        {/* Always mounted so expand/collapse can animate: the 1fr/0fr grid row transition is the
            CSS-only way to animate to an auto height (crit c_4b5e87). */}
        <div
          aria-hidden={!isExpanded}
          style={{
            display: "grid",
            gridTemplateRows: isExpanded ? "1fr" : "0fr",
            transition: "grid-template-rows 300ms ease",
          }}
        >
          <div
            style={{
              overflow: "hidden",
              minHeight: 0,
              visibility: isExpanded ? "visible" : "hidden",
              transition: "visibility 300ms",
            }}
          >
            <div className="p-[16px]">
              <div className={clsx("grid gap-[16px]", isHr ? "grid-cols-3" : "grid-cols-2")}>
                <DataFlowSubCard
                  stepNumber={1}
                  title={"Data Categories"}
                  description={
                    isCollectionFinished
                      ? "Review your collected data categories"
                      : "Choose the data categories you collect"
                  }
                  status={isCollectionFinished ? "complete" : "incomplete"}
                  targetRoute={config.collectionRouteForGroup(collectionGroup.id)}
                />
                <DataFlowSubCard
                  stepNumber={2}
                  title={"Vendor Disclosures"}
                  description={
                    isDisclosureFinished
                      ? "Review your Data Recipients and Disclosures"
                      : "Match each category to its vendors"
                  }
                  status={isDisclosureFinished ? "complete" : "incomplete"}
                  targetRoute={config.disclosuresRouteForGroup(collectionGroup.id)}
                  isDisabled={!isCollectionFinished}
                  disabledTooltip="Complete Step 1 to unlock this step"
                />
                {isHr && (
                  <DataFlowSubCard
                    stepNumber={3}
                    title={"Processing Purposes"}
                    description={
                      isPurposesFinished
                        ? "Review Processing purposes"
                        : "Select the purposes for the data use"
                    }
                    status={isPurposesFinished ? "complete" : "incomplete"}
                    targetRoute={config.purposesRouteForGroup(collectionGroup.id)}
                    isDisabled={!isCollectionFinished || !isDisclosureFinished}
                    disabledTooltip={
                      !isCollectionFinished && !isDisclosureFinished
                        ? "Complete Steps 1 and 2 to unlock this step"
                        : !isCollectionFinished
                        ? "Complete Step 1 to unlock this step"
                        : "Complete Step 2 to unlock this step"
                    }
                  />
                )}
              </div>

              {isAllComplete && shouldRenderBody && (
                /* No inner scroll — the diagrams grow the panel and scroll with the page. */
                <div>
                  {filteredPicGroups.map((group) => {
                    let diagramCategories = group.categories
                      .filter((cat) => collected.includes(cat.id))
                      .map((cat) => ({ id: cat.id, name: cat.name }));

                    if (diagramCategories.length == 0) {
                      return null;
                    }

                    const purposes = isHr
                      ? []
                      : selectedConsumerPurposes(defCheckboxes, activities);

                    return (
                      <div className="mt-[20px]" key={group.id}>
                        <DataFlowsDiagram
                          org={org}
                          picGroupName={group.name}
                          categories={diagramCategories}
                          sources={receivedData.sources
                            .filter((source) => {
                              let selectedPics = receivedPICsFromFilteredReceived(dataMap, {
                                collectionGroupId: collectionGroup.id,
                                vendorId: source.vendorId,
                              }).filter((pic) =>
                                group.categories.map((g) => g.id).includes(pic.id),
                              );
                              return selectedPics.length > 0;
                            })
                            .map((source) => ({
                              name:
                                isHr && source.vendorKey === SOURCE_KEY_CONSUMER_DIRECTLY
                                  ? "Worker"
                                  : source.name,
                              logoUrl: source.logoUrl,
                            }))}
                          processingVendors={disclosureData.dataRecipients.filter((vendor) => {
                            let selectedPicGroups = disclosedPICGroupsFromFilteredDisclosed(
                              dataMap,
                              {
                                vendorId: vendor.vendorId,
                                collectionGroupId: collectionGroup.id,
                              },
                            );
                            return selectedPicGroups.map((g) => g.id).includes(group.id);
                          })}
                          processingPurposes={purposes}
                          activities={activities ?? []}
                          showPurposes={!isHr}
                          // The finished map doubles as navigation: each row opens the step that
                          // owns it, focused on the thing that was clicked.
                          onCategoryClick={(categoryId) =>
                            history.push(
                              `${config.collectionRouteForGroup(
                                collectionGroup.id,
                              )}?category=${categoryId}`,
                            )
                          }
                          onVendorClick={(vendorId) =>
                            history.push(
                              `${config.disclosuresRouteForGroup(
                                collectionGroup.id,
                              )}?vendor=${vendorId}`,
                            )
                          }
                          onPurposeClick={(purposeId) =>
                            history.push(
                              `${config.purposesRouteForGroup(
                                collectionGroup.id,
                              )}?purpose=${purposeId}`,
                            )
                          }
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function DataFlowsDashboardPage() {
  const orgId = usePrimaryOrganizationId();
  const [groups, , refreshGroups] = useNonEmploymentCollectionGroups(
    orgId,
    /* onlyUseSettled = */ true,
  );

  return (
    <DataFlowsDashboardPageContent
      config={CONSUMER_DATA_FLOWS_DASHBOARD_CONFIG}
      groups={groups}
      refreshGroups={refreshGroups}
    />
  );
}

export function HRDataFlowsDashboardContent() {
  const orgId = usePrimaryOrganizationId();
  const [groups, , refreshGroups] = useEmploymentCollectionGroups(
    orgId,
    /* onlyUseSettled = */ true,
  );

  return (
    <DataFlowsDashboardPageContent
      config={HR_DATA_FLOWS_DASHBOARD_CONFIG}
      groups={groups}
      refreshGroups={refreshGroups}
      includePageWrapper={false}
    />
  );
}

export function DataFlowsDashboardPageContent({
  config,
  groups,
  refreshGroups,
  includePageWrapper = true,
}: {
  config: DataFlowsDashboardPageConfig;
  groups: CollectionGroupDetailsDto[];
  refreshGroups: () => void;
  includePageWrapper?: boolean;
}) {
  const history = useHistory();
  const showErrorSnackbar = useSnackBar();

  const orgId = usePrimaryOrganizationId();
  const { data: dataMap, refresh: refreshDataMap } = useDataMap(orgId);

  const { create: createDefaultCollectionGroups } = useCreateDefaultCollectionGroups(orgId);
  const [isCreatingCollectionGroup, setIsCreatingCollectionGroup] = useState(false);
  const [addCollectionGroupModalOpen, setAddCollectionGroupModalOpen] = useState(false);

  // Footer narration inputs. Sources completeness only matters on the consumer dashboard
  // (HR has no sources tab and brings its own footer), but the hooks run unconditionally.
  const isConsumer = !config.collectionContext.includes("EMPLOYEE");
  const stepsCompletion = useDataFlowCollectionGroupsCompletion({ config, groups });
  const sourcesCompletion = useDataFlowsSourcesCompletion(orgId, isConsumer ? groups : []);
  const [onboardingProgress] = useOnboardingProgress(orgId);
  const [primaryOrg] = usePrimaryOrganization();
  const invalidateOnboardingProgress = useInvalidateOnboardingProgress(orgId);
  // Refresh the flow-level progress once per dashboard visit so nav badges and the footer
  // reflect edits made on the child pages without a full reload.
  useEffect(() => {
    invalidateOnboardingProgress();
  }, [invalidateOnboardingProgress]);

  if (!groups || !dataMap) {
    return <PageLoading />;
  }

  const tabsConfig = [
    {
      name: "Collection Groups",
      tabKey: "collection-groups",
      route: config.dashboardRoute,
      current: true,
    },
    // Consumer purposes are org-wide, so they sit between the groups and Sources as their own
    // tab (rather than a third step inside every group). Locked until every group's two steps
    // are done; a finished org keeps free navigation even if a group later turns incomplete.
    ...(isConsumer && config.purposesRoute
      ? [
          {
            name: "Purposes",
            tabKey: "purposes",
            route: config.purposesRoute,
            current: false,
            showIncompleteIndicator: stepsCompletion.isReady && !stepsCompletion.purposesComplete,
            disabled:
              primaryOrg?.finishedOnboardingLatch !== true &&
              stepsCompletion.isReady &&
              !stepsCompletion.allComplete,
          },
        ]
      : []),
    ...(config.sourcesRoute
      ? [
          {
            name: "Sources",
            tabKey: "sources",
            route: config.sourcesRoute,
            current: false,
            showIncompleteIndicator:
              sourcesCompletion.isReady && !sourcesCompletion.isSourcesComplete,
            // Sources is the flow's last stop — until every group's steps and the purposes are
            // done the tab is locked, like its step chip below (POL-7818).
            disabled:
              primaryOrg?.finishedOnboardingLatch !== true &&
              stepsCompletion.isReady &&
              !(stepsCompletion.allComplete && stepsCompletion.purposesComplete),
          },
        ]
      : []),
  ];

  const refreshGroupsAndDataMap = () => {
    refreshGroups();
    refreshDataMap();
  };

  // Help-card shortcuts target the first collection group and unlock in step order, matching
  // that group's step cards. With no group yet there is nothing to link to.
  const firstGroup = groups[0];
  const firstGroupSteps = stepsCompletion.firstGroupCompletion;
  const helpCardSteps = firstGroup
    ? [
        {
          label: "What data you collect",
          route: config.collectionRouteForGroup(firstGroup.id),
          isLocked: false,
          lockedTooltip: undefined as string | undefined,
        },
        {
          label: "Who handles it",
          route: config.disclosuresRouteForGroup(firstGroup.id),
          isLocked: !firstGroupSteps?.isCollectionFinished,
          lockedTooltip: "Complete Step 1 to unlock this step",
        },
        // HR purposes are answered per group, so they stay a numbered step; consumer purposes
        // are org-wide and get their own unnumbered chip after the groups (see below).
        ...(isConsumer
          ? []
          : [
              {
                label: "How it's used",
                route: config.purposesRouteForGroup(firstGroup.id),
                isLocked:
                  !firstGroupSteps?.isCollectionFinished || !firstGroupSteps?.isDisclosureFinished,
                lockedTooltip:
                  !firstGroupSteps?.isCollectionFinished && !firstGroupSteps?.isDisclosureFinished
                    ? "Complete Steps 1 and 2 to unlock this step"
                    : !firstGroupSteps?.isCollectionFinished
                    ? "Complete Step 1 to unlock this step"
                    : "Complete Step 2 to unlock this step",
              },
            ]),
      ]
    : [];
  const allGroupsComplete = stepsCompletion.allComplete && stepsCompletion.purposesComplete;

  const content = (
    <div className="tw-boundary">
      <DataFlowsStickyHeader
        breadcrumb={
          config.showBreadcrumbs !== false
            ? {
                title: config.breadcrumbTitle,
                source: config.breadcrumbSource,
                sourceRoute: config.breadcrumbSourceRoute,
              }
            : undefined
        }
        tabs={tabsConfig}
      />
      {groups.length > 0 ? (
        <div style={{ maxWidth: DATA_FLOWS_CONTENT_MAX_WIDTH }} className="pt-[24px]">
          {addCollectionGroupModalOpen && orgId && (
            <AddCollectionGroupModal
              organizationId={orgId}
              existingGroups={groups}
              collectionGroupType={config.collectionGroupType}
              onClose={() => setAddCollectionGroupModalOpen(false)}
              onCreated={() => {
                setAddCollectionGroupModalOpen(false);
                refreshGroupsAndDataMap();
              }}
            />
          )}
          <DataFlowsHelpCard
            title="Build a data flow for each Collection Group"
            body={
              isConsumer
                ? "A data flow maps how personal data moves through your business — it powers your data map and privacy policy. Work through both steps for each group, then choose your Purposes and confirm your data Sources once for all groups:"
                : "A data flow maps how personal data moves through your business — it powers your data map and privacy policy. Work through the three steps in order for each group below:"
            }
          >
            {/* The step chips are shortcuts into the flow for the first group, unlocked in the
                same order as that group's step cards. */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 8,
                marginTop: 12,
              }}
            >
              {helpCardSteps.map((step, i, arr) => (
                <React.Fragment key={step.label}>
                  <StepChip
                    stepNumber={i + 1}
                    label={step.label}
                    route={step.route}
                    isLocked={step.isLocked}
                    lockedTooltip={step.lockedTooltip}
                  />
                  {i < arr.length - 1 && (
                    <ChevronRightIcon className="h-4 w-4" style={{ color: "#726D83" }} />
                  )}
                </React.Fragment>
              ))}
              {/* The org-wide stages aren't per-group steps — quiet, unnumbered chips keep the
                  "2 steps, then Purposes and Sources" story honest without inventing Steps 3–4. */}
              {isConsumer && config.purposesRoute && (
                <>
                  <span style={{ fontSize: 14, color: "#726D83" }}>then</span>
                  <StepChip
                    label="Purposes — how it's used"
                    route={config.purposesRoute}
                    isLocked={!stepsCompletion.allComplete}
                    lockedTooltip="Complete both steps for each Collection Group to unlock Purposes"
                  />
                </>
              )}
              {isConsumer && config.sourcesRoute && (
                <>
                  <ChevronRightIcon className="h-4 w-4" style={{ color: "#726D83" }} />
                  <StepChip
                    label="Sources — where it comes from"
                    route={config.sourcesRoute}
                    isLocked={!allGroupsComplete}
                    lockedTooltip="Complete each Collection Group and your Purposes to unlock Sources"
                  />
                </>
              )}
            </div>
            <div style={{ marginTop: 12, fontSize: 14, lineHeight: "21px", color: "#726D83" }}>
              {config.collectionGroupType === "EMPLOYMENT"
                ? "Collect data about another workforce audience, like job applicants or contractors?"
                : "Collect data about another audience, like newsletter subscribers?"}{" "}
              <button
                type="button"
                onClick={() => setAddCollectionGroupModalOpen(true)}
                className="cursor-pointer font-medium hover:underline"
                style={{ color: "#5331C6", background: "none", border: "none", padding: 0 }}
              >
                {config.collectionGroupType === "EMPLOYMENT"
                  ? "Add another HR Collection Group"
                  : "Add another Collection Group"}
              </button>
            </div>
          </DataFlowsHelpCard>
          <div>
            {groups.map((group) => {
              return (
                <ConsumersPanel
                  key={group.id}
                  dataMap={dataMap}
                  collectionGroup={group}
                  allCollectionGroups={groups}
                  config={config}
                  onCollectionGroupsUpdated={refreshGroupsAndDataMap}
                />
              );
            })}
          </div>
          <div className="pb-[24px]" />
        </div>
      ) : config.showConsumerOnboarding ? (
        // Card shell matches the vendor inventory onboarding scan card (pol-7592): hairline
        // border + subtle shadow. Sizing lives in an inline style because new Tailwind
        // arbitrary values (w-[560px] etc.) aren't in the compiled utility bundle.
        <div
          className="bg-white border border-solid border-neutral-200 rounded-xl shadow-[0px_1px_2px_0px_#1018280D]"
          style={{ width: 560, maxWidth: "100%", margin: "120px auto 0", padding: 32 }}
        >
          <div
            className="flex items-center justify-center rounded-full"
            style={{ width: 48, height: 48, backgroundColor: "#F3F0FE" }}
          >
            <UserGroupIcon className="h-6 w-6" style={{ color: "#3C1DA5" }} />
          </div>
          <div className="mt-[16px] text-[18px] font-semibold text-neutral-900">
            Set up your first Collection Group
          </div>
          <div className="mt-[4px] text-[14px] leading-[21px] text-neutral-700">
            Collection Groups are the categories of people you collect data about. We'll start you
            off with a default group, “Consumers” — rename it or add more anytime.
          </div>
          <div className="mt-[12px] text-[14px] leading-[21px] text-neutral-700">
            Each group is the starting point for a data flow: what data you collect, where it goes,
            and why.
          </div>

          <div className="mt-[20px] flex items-center gap-[12px]">
            <Button
              color="primary"
              onClick={async () => {
                setIsCreatingCollectionGroup(true);
                try {
                  // One-click onboarding: create the default Consumers group, then seed its
                  // default vendor associations so the refreshed page lands straight on the
                  // real Data Flows view instead of a second onboarding step. recreateDeleted:
                  // an org that deleted its Consumers group lands here too, and without it the
                  // backend treats the slug as already created and does nothing.
                  await createDefaultCollectionGroups({
                    collectionGroupTypes: [config.collectionGroupType],
                    recreateDeleted: true,
                  });
                  const consumer = await Api.consumerGroups.getConsumerCollectionGroup(orgId);
                  if (consumer) {
                    await Api.consumerGroups.createDefaultRecipientAssociations(orgId, consumer.id);
                  }
                  refreshGroupsAndDataMap();
                } catch (e) {
                  showErrorSnackbar("Couldn't create the group. Please try again.", {
                    variant: "error",
                  });
                } finally {
                  setIsCreatingCollectionGroup(false);
                }
              }}
              disabled={isCreatingCollectionGroup}
            >
              Create my Consumers group
            </Button>
            {isCreatingCollectionGroup && <Loading />}
          </div>

          <div className="mt-[24px] pt-[16px] border-t border-solid border-neutral-200 text-[13px] text-neutral-700">
            Don't add employee or job applicant groups here — you'll map those separately in the HR
            Center.
          </div>
        </div>
      ) : (
        <div className="mx-auto max-w-[800px]">
          <div className="mt-[60px] text-[30px] font-bold leading-[45px] text-neutral-800">
            {config.emptyStateTitle}
          </div>
          <div className="mt-[24px] text-[20px] font-medium leading-[30px] text-neutral-700">
            {config.emptyStateBody}
          </div>
        </div>
      )}
    </div>
  );

  if (!includePageWrapper) {
    return content;
  }

  // Onboarding footer: the left status text narrates the next action through the whole flow
  // (steps per group, then Sources), the button is the single forward CTA. Kept visible for
  // the whole data-map onboarding stretch, mirroring the vendor inventory footer (DES-30).
  const isDataFlowsOnboardingIncomplete =
    onboardingProgress != null && !onboardingProgress.dataMapOnboardingProgress.isDataFlowsComplete;
  // Once pol-7563's latch is set the org is out of onboarding, so the wizard framing ("Next: …
  // Step 1 of 3", "Continue to Sources") is wrong even when a later-added group leaves work
  // outstanding — the per-group step cards still show what's incomplete. The child step pages
  // suppress their footers on the same signal (see useDataFlowsOnboardingFooterGate).
  const hasFinishedOnboarding = primaryOrg?.finishedOnboardingLatch === true;

  let footer: React.ReactNode = null;
  if (
    isConsumer &&
    config.sourcesRoute &&
    isDataFlowsOnboardingIncomplete &&
    !hasFinishedOnboarding
  ) {
    const groupsReady = stepsCompletion.isReady;
    const groupsComplete =
      groupsReady && stepsCompletion.allComplete && stepsCompletion.purposesComplete;
    const next = stepsCompletion.nextIncomplete;

    let statusText: React.ReactNode = null;
    if (groups.length === 0) {
      statusText = (
        <>
          <span style={{ color: "#726D83" }}>Next:</span> create your Consumers group to get started
        </>
      );
    } else if (groupsReady && next != null && next.collectionGroup == null) {
      statusText = (
        <>
          <span style={{ color: "#726D83" }}>Next:</span> choose how your data is used{" "}
          <span style={{ color: "#726D83" }}>(Purposes)</span>
        </>
      );
    } else if (groupsReady && next != null && next.collectionGroup != null) {
      const groupName = next.collectionGroup.name;
      const possessive = groupName.endsWith("s") ? `${groupName}’` : `${groupName}’s`;
      const stepText =
        next.step === 1
          ? `select the data ${groupName} collects`
          : `match ${possessive} data to the vendors that handle it`;
      statusText = (
        <>
          <span style={{ color: "#726D83" }}>Next:</span> {stepText}{" "}
          <span style={{ color: "#726D83" }}>(Step {next.step} of 2)</span>
        </>
      );
    } else if (groupsComplete && !sourcesCompletion.isSourcesComplete) {
      statusText = (
        <span className="flex items-center gap-[6px]">
          <CheckIcon className="h-[16px] w-[16px] text-green-700" />
          Collection groups and purposes complete
        </span>
      );
    } else if (groupsComplete && sourcesCompletion.isSourcesComplete) {
      statusText = (
        <span className="flex items-center gap-[6px]">
          <CheckIcon className="h-[16px] w-[16px] text-green-700" />
          Data Flows complete
        </span>
      );
    }

    const isEverythingComplete = groupsComplete && sourcesCompletion.isSourcesComplete;
    const areGroupStepsComplete = groupsReady && stepsCompletion.allComplete;
    // The flow runs collection groups → Purposes → Sources, so the CTA points at whichever of
    // those is next; until the group steps are done it waits on Purposes.
    const nextStep = isEverythingComplete
      ? { label: "Done with Data Flows", route: Routes.dataMap.dashboard }
      : groupsComplete
      ? { label: "Continue to Sources", route: config.sourcesRoute }
      : { label: "Continue to Purposes", route: config.purposesRoute ?? config.sourcesRoute };
    footer = (
      <ProgressFooter
        // .progress-footer-content sets 18px, so the size has to be inline to win.
        content={
          statusText && (
            <span className="tw-boundary" style={{ fontSize: 14 }}>
              {statusText}
            </span>
          )
        }
        actions={
          <ProgressFooterActions
            nextContent={nextStep.label}
            nextOnClick={() => {
              invalidateOnboardingProgress();
              history.push(nextStep.route);
            }}
            nextDisabled={!areGroupStepsComplete}
            nextDisabledTooltip="Complete both steps for each collection group first"
          />
        }
      />
    );
  }

  return (
    <StandardPageWrapper fullWidth footer={footer}>
      {content}
    </StandardPageWrapper>
  );
}
